import { Label } from '../../label';
import type { LabelNode } from 'data-layer';
import { TEXT_VARIANT } from '@pixum/text';
import { getIconByName } from '../../icons/getIconByName';
import type { ReactElement } from 'react';
import type { KeyVisualFootnotesProps } from '../types';

/**
 * Gets the appropriate icon component for a footnote based on its asset identifier.
 *
 * The asset identifier should be the icon name from the centralized icon registry
 * (e.g., 'icon-check', 'icon-shield', 'icon-bell', etc.)
 *
 * @param asset - The icon name from Contentful (e.g., 'icon-check', 'icon-shield')
 * @returns React icon component or undefined if not found
 */
function getFootnoteIcon(asset?: string): ReactElement | undefined {
  if (!asset) return undefined;

  // Pass the asset directly to getIconByName with color and size
  return getIconByName(asset, 'var(--color-accent)');
}

/**
 * KeyVisualFootnotes component - Renders footnote labels with icons.
 *
 * Displays a horizontal list of footnote labels, typically used for trust indicators
 * like "Virus-checked" and "Free" on download pages. Icons are resolved from the
 * centralized icon registry via getIconByName.
 *
 * **Important**: The `asset` field in footnotes should contain the icon name exactly
 * as defined in `getIconByName` (e.g., 'icon-check', 'icon-shield', 'icon-bell').
 *
 * @param props - Footnotes configuration
 * @returns Rendered footnote labels or null if no footnotes provided
 *
 * @example
 * // Basic footnotes for download page
 * <KeyVisualFootnotes
 *   footnotes={[
 *     { sys: { id: '1' }, text: 'Virus-checked', asset: 'icon-shield' },
 *     { sys: { id: '2' }, text: 'Free', asset: 'icon-check' }
 *   ]}
 * />
 *
 * @example
 * // No footnotes (renders nothing)
 * <KeyVisualFootnotes footnotes={[]} />
 *
 * @example
 * // With different icons from CMS
 * <KeyVisualFootnotes
 *   footnotes={[
 *     { sys: { id: '1' }, text: 'Secure Download', asset: 'icon-shield' },
 *     { sys: { id: '2' }, text: 'Verified Software', asset: 'icon-check' },
 *     { sys: { id: '3' }, text: 'Notifications', asset: 'icon-bell' }
 *   ]}
 * />
 */
export function KeyVisualFootnotes({ footnotes }: KeyVisualFootnotesProps) {
  // Don't render if no footnotes provided
  if (!footnotes || footnotes.length === 0) {
    return null;
  }

  return (
    <div className="key-visual__footnotes">
      {footnotes.map((footnote: LabelNode) => {
        // Safety check: skip invalid footnotes
        if (!footnote?.sys?.id) return null;

        return (
          <Label
            key={footnote.sys.id}
            icon={getFootnoteIcon(footnote.asset)}
            text={footnote.text || ''}
            showIcon={true}
            variant={TEXT_VARIANT.FOOTNOTE}
          />
        );
      })}
    </div>
  );
}
